/**
 * AuthCore - Plain TypeScript authentication service
 *
 * Transport-agnostic service that manages token state and refresh logic.
 * No dependencies on Apollo, React, or any framework.
 */

export type RefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false };

export class AuthCore {
  private accessToken: string | null = null;
  private refreshInFlight: Promise<RefreshResult> | null = null;
  private subscribers = new Set<() => void>();

  constructor(
    private doRefresh: () => Promise<RefreshResult>,
    private doLogout: () => Promise<void>
  ) {}

  getToken(): string | null {
    return this.accessToken;
  }

  setToken(token: string | null): void {
    this.accessToken = token;
    this.subscribers.forEach(fn => fn());
  }

  onChange(fn: () => void): () => void {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  /**
   * Ensures token is fresh. Deduplicates concurrent refresh requests.
   * If refresh fails, forces logout.
   */
  async ensureFreshToken(): Promise<RefreshResult> {
    // Deduplicate: if refresh already in flight, await it
    if (!this.refreshInFlight) {
      this.refreshInFlight = (async () => {
        try {
          return await this.doRefresh();
        } finally {
          this.refreshInFlight = null;
        }
      })();
    }

    const result = await this.refreshInFlight;

    if (result.ok) {
      this.setToken(result.accessToken);
    } else {
      await this.forceLogout();
    }

    return result;
  }

  async forceLogout(): Promise<void> {
    this.setToken(null);
    await this.doLogout();
  }
}
